import PropTypes from "prop-types";

import { Box, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
  UpstreamTransactionLink,
} from "@/features/ui";

export const ProductVariantsSection = ({ salesInvoiceProductVariants }) => {
  const { hasPermission } = useAuth();

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} linkToStock />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 1,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "salesOrder",
      headerName: "Pesanan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { salesOrder } }) => (
        <UpstreamTransactionLink
          referenceNumber={salesOrder?.referenceNumber}
          quantity={salesOrder?.quantity}
          clickable={hasPermission(PermissionEnum.READ_SALES_ORDERS)}
        />
      ),
    },
    {
      field: "deliveryOrders",
      headerName: "Dikirim",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.deliveryOrders}
          clickable={hasPermission(PermissionEnum.READ_DELIVERY_ORDERS)}
        />
      ),
    },
    {
      field: "salesReturns",
      headerName: "Retur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesReturns}
          clickable={hasPermission(PermissionEnum.READ_SALES_RETURNS)}
        />
      ),
    },
    {
      field: "price",
      headerName: "Harga",
      type: "number",
      flex: 1.5,
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "subtotal",
      headerName: "Total",
      flex: 2,
      type: "number",
      valueGetter: (_, row) => row.quantity * row.price - row.discount,
      renderCell: ({ row }) => {
        const { quantity, price, discount } = row;
        const total = quantity * price - discount;
        const hasDiscount = discount && discount > 0;

        if (!hasDiscount) {
          return (
            <Typography variant="body2">{formatNumber(total)}</Typography>
          );
        }

        const originalTotal = quantity * price;

        return (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatNumber(total)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textDecoration: "line-through",
                color: "text.secondary",
                fontSize: "0.7rem",
              }}
            >
              {formatNumber(originalTotal)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 1.5,
      valueGetter: (_, row) => row.note || "-",
    },
  ];

  return (
    <Stack spacing={2}>
      <DataGrid
        getRowHeight={() => "auto"}
        columns={columns}
        rows={salesInvoiceProductVariants}
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
      />
    </Stack>
  );
};

ProductVariantsSection.propTypes = {
  salesInvoiceProductVariants: PropTypes.array.isRequired,
};
